import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import logger from '../utils/logger';
import i18n from '../i18n';

export const useUserData = () => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [role, setRole] = useState(() => localStorage.getItem('userRole') || null);
    const [roleLoading, setRoleLoading] = useState(true); // Tracks if role is still being fetched
    const [loading, setLoading] = useState(true);
    const [isProfileComplete, setIsProfileComplete] = useState(null); // null = loading/unknown
    const [userLoadError, setUserLoadError] = useState(null);
    const [pendingDeletion, setPendingDeletion] = useState(null);

    useEffect(() => {
        let unsubscribeSnapshot = null;

        const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
            // Clean up previous snapshot listener if exists
            if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
                unsubscribeSnapshot = null;
            }

            setUser(currentUser);
            setUserLoadError(null); // Reset error on auth change

            if (currentUser) {
                // Reset profile status to loading/unknown while we fetch data
                setIsProfileComplete(null);

                logger.authLogin(currentUser.uid, 'google');

                try {
                    // 1. Fetch Role (One-time fetch is usually enough for role, but could be real-time too)
                    const roleDocRef = doc(db, 'userRoles', currentUser.uid);
                    const roleSnap = await getDoc(roleDocRef);

                    if (roleSnap.exists()) {
                        const { role: storedRole } = roleSnap.data();
                        setRole(storedRole);
                        localStorage.setItem('userRole', storedRole);
                    } else {
                        setRole(null);
                        localStorage.removeItem('userRole');
                    }
                    setRoleLoading(false); // Role fetch complete

                    const setupSubscription = (retryCount = 0) => {
                        const userDocRef = doc(db, 'users', currentUser.uid);

                        unsubscribeSnapshot = onSnapshot(userDocRef, (docSnap) => {
                            if (docSnap.exists()) {
                                const userData = docSnap.data();
                                setProfile(userData);

                                // Update lastActive if not updated in this session
                                const lastSessionUpdate = sessionStorage.getItem(`lastActive_${currentUser.uid}`);
                                const now = Date.now();
                                if (!lastSessionUpdate || now - parseInt(lastSessionUpdate) > 1000 * 60 * 30) { // Update every 30 mins max
                                    import('firebase/firestore').then(({ updateDoc, serverTimestamp }) => {
                                        updateDoc(userDocRef, { lastActive: serverTimestamp() }).catch(e => console.error("Error updating lastActive:", e));
                                        sessionStorage.setItem(`lastActive_${currentUser.uid}`, now.toString());
                                    });
                                }

                                // Set Language
                                const searchParams = new URLSearchParams(window.location.search);
                                const hasLangParam = searchParams.has('lng');

                                if (!hasLangParam && userData.preferredLanguage && i18n.language !== userData.preferredLanguage) {
                                    i18n.changeLanguage(userData.preferredLanguage);
                                }

                                // Check Deletion Status
                                if (userData.accountStatus === 'pending_deletion') {
                                    setPendingDeletion({
                                        scheduledAt: userData.deletionScheduledAt,
                                        executionAt: userData.deletionExecutionAt
                                    });
                                } else {
                                    setPendingDeletion(null);
                                }


                                // Check if profile is actually complete (has required fields)
                                // Required fields: displayName, birthdate (checking both camel and snake case for legacy)
                                const birthDateValue = userData.birthdate || userData.birthDate;
                                const hasRequiredFields = !!(userData.displayName && birthDateValue);

                                setIsProfileComplete(hasRequiredFields);
                                setLoading(false);
                            } else {
                                // Document does not exist yet (common for new users)
                                // Don't error out, just wait for it to be created eventually
                                // We might want to keep loading true for a split second? 
                                // userLoadError is NOT set here because it's not an error.
                                setProfile(null);
                                setIsProfileComplete(false);
                                setLoading(false);
                            }
                        }, (error) => {
                            logger.error(`Error listening to user data (Attempt ${retryCount + 1})`, error);

                            // Retry logic
                            if (retryCount < 3) {
                                console.log(`Retrying user data subscription in 1.5s... (Attempt ${retryCount + 1})`);
                                setTimeout(() => {
                                    if (auth.currentUser?.uid === currentUser.uid) { // Ensure user hasn't changed
                                        setupSubscription(retryCount + 1);
                                    }
                                }, 1500);
                            } else {
                                // Final failure
                                setUserLoadError(error);
                                setIsProfileComplete(false);
                                setLoading(false);
                            }
                        });
                    };

                    setupSubscription();

                } catch (error) {
                    logger.error('Error setting up user data listener', error);
                    setUserLoadError(error);
                    setIsProfileComplete(false);
                    setLoading(false);
                }
            } else {
                // User logged out
                setRole(null);
                setRoleLoading(true); // Reset for next login
                setProfile(null);
                localStorage.removeItem('userRole');
                setIsProfileComplete(false); // Explicitly false when logged out
                setPendingDeletion(null);
                setLoading(false);
            }
        });

        return () => {
            if (unsubscribeSnapshot) unsubscribeSnapshot();
            unsubscribeAuth();
        };
    }, []);

    const updateRole = async (newRole) => {
        setRole(newRole);
        if (user) {
            try {
                const roleRef = doc(db, 'userRoles', user.uid);
                await setDoc(roleRef, { role: newRole }, { merge: true });
                localStorage.setItem('userRole', newRole);
                logger.debug('Role updated successfully', { uid: user.uid, newRole });
            } catch (error) {
                console.error('Error updating role:', error);
                logger.error('Error updating role persistence', error);
                throw error; // Re-throw to let UI handle it
            }
        }
    };

    const markProfileComplete = () => {
        setIsProfileComplete(true);
    };

    const clearPendingDeletion = () => {
        setPendingDeletion(null);
    };

    return {
        user,
        profile,
        role,
        roleLoading,
        loading,
        isProfileComplete,
        pendingDeletion,
        userLoadError,
        updateRole,
        markProfileComplete,
        clearPendingDeletion
    };
};
